import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import GateConfigMenu from '../../components/GateConfigMenu';
import GateScheduleEditor from '../../components/GateScheduleEditor';
import GatesTable from '../../components/GatesTable';
import CustomerBreadcrumb from '../../components/CustomerBreadcrumb';

/**
 * Site-scoped gate groups: list, create, rename, delete, and per-group
 * configuration. Same component is mounted for the customer (apiBase
 * = "customer", URL prefix /sites/:id/groups) and the admin (apiBase
 * = "admin", URL prefix /sites/:id/groups under AdminDashboard).
 */

function GateGroupsPage({ apiBase = 'customer' }) {
  const { id } = useParams();   // site id
  const [groups, setGroups] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [error, setError] = useState(null);

  const load = () => {
    axios.get(`/api/${apiBase}/sites/${id}/gate-groups`)
      .then(res => setGroups(res.data))
      .catch(() => setError('שגיאה בטעינת הקבוצות'));
  };

  useEffect(load, [id, apiBase]);

  const create = (e) => {
    e.preventDefault();
    setError(null);
    axios.post(`/api/${apiBase}/sites/${id}/gate-groups`, {
      name: newName,
      description: newDesc || null,
    }).then(() => {
      setCreating(false);
      setNewName(''); setNewDesc('');
      load();
    }).catch(err => setError(err.response?.data?.error || 'שגיאה ביצירה'));
  };

  if (groups === null) return <div className="loading">טוען...</div>;

  return (
    <>
      {apiBase === 'customer' ? (
        <CustomerBreadcrumb items={[
          { label: 'מפת האתר', to: `/sites/${id}` },
          { label: 'קבוצות' },
        ]} />
      ) : (
        <div style={{ marginBottom: '0.6rem' }}>
          <Link to={`/sites/${id}`} style={{ fontSize: '0.85rem', color: '#6b7280', textDecoration: 'none' }}>
            ← חזרה למפת האתר
          </Link>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 600, margin: 0 }}>קבוצות שערים</h2>
        {!creating && (
          <button
            onClick={() => setCreating(true)}
            style={{
              padding: '0.45rem 0.9rem',
              border: '1px solid #d1d5db',
              background: '#1f2937',
              color: '#fff',
              borderRadius: '6px',
              fontSize: '0.85rem',
              cursor: 'pointer',
            }}
          >
            + קבוצה חדשה
          </button>
        )}
      </div>

      {creating && (
        <form onSubmit={create} style={{
          padding: '1rem',
          border: '1px solid #e5e7eb',
          borderRadius: '6px',
          marginBottom: '1.25rem',
          background: '#f9fafb',
        }}>
          <div style={{ marginBottom: '0.65rem' }}>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '0.2rem' }}>
              שם הקבוצה *
            </label>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              autoFocus
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '0.45rem 0.65rem',
                border: '1px solid #d1d5db', borderRadius: '5px', fontSize: '0.9rem',
              }}
              placeholder="לדוגמה: רציפי טעינה צפון"
            />
          </div>
          <div style={{ marginBottom: '0.85rem' }}>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '0.2rem' }}>
              תיאור (אופציונלי)
            </label>
            <input
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '0.45rem 0.65rem',
                border: '1px solid #d1d5db', borderRadius: '5px', fontSize: '0.9rem',
              }}
            />
          </div>
          {error && <div style={{ color: '#dc2626', fontSize: '0.85rem', marginBottom: '0.65rem' }}>{error}</div>}
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => { setCreating(false); setError(null); }}
              style={{ padding: '0.4rem 0.85rem', background: 'transparent', border: 'none', color: '#6b7280', cursor: 'pointer' }}
            >
              ביטול
            </button>
            <button
              type="submit"
              style={{
                padding: '0.4rem 0.95rem', background: '#1f2937', color: '#fff',
                border: 'none', borderRadius: '5px', fontSize: '0.85rem', cursor: 'pointer',
              }}
            >
              צור
            </button>
          </div>
        </form>
      )}

      {groups.length === 0 && !creating && (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af', fontSize: '0.9rem' }}>
          אין קבוצות עדיין. צור קבוצה ראשונה כדי לארגן את השערים שלך לפי תפקיד.
        </div>
      )}

      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {groups.map(g => (
          <li key={g.id}>
            <Link
              to={apiBase === 'customer' ? `/sites/${id}/groups/${g.id}` : `/sites/${id}/groups/${g.id}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0.95rem 1.1rem',
                border: '1px solid #e5e7eb',
                background: '#fff',
                borderRadius: '6px',
                marginBottom: '0.5rem',
                textDecoration: 'none',
                color: '#1a1a2e',
              }}
            >
              <div>
                <div style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.15rem' }}>{g.name}</div>
                {g.description && <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '0.2rem' }}>{g.description}</div>}
                <div style={{ fontSize: '0.78rem', color: '#9ca3af' }}>{g.gate_count} שערים</div>
              </div>
              <span style={{ color: '#9ca3af', fontSize: '1.1rem' }}>‹</span>
            </Link>
          </li>
        ))}
      </ul>
    </>
  );
}


export function GateGroupDetailPage({ apiBase = 'customer' }) {
  const { id, gid } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [siteGates, setSiteGates] = useState([]);
  const [groupSettings, setGroupSettings] = useState(null);
  const [editingMeta, setEditingMeta] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [draftDesc, setDraftDesc] = useState('');
  const [configOpen, setConfigOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [showAddGates, setShowAddGates] = useState(false);
  const [error, setError] = useState(null);

  const load = () => {
    axios.get(`/api/${apiBase}/gate-groups/${gid}`)
      .then(res => {
        setGroup(res.data);
        setDraftName(res.data.name);
        setDraftDesc(res.data.description || '');
      })
      .catch(() => setError('שגיאה בטעינת הקבוצה'));
    axios.get(`/api/${apiBase}/sites/${id}`)
      .then(res => setSiteGates(res.data.gates || []));
    axios.get(`/api/${apiBase}/gate-groups/${gid}/settings`)
      .then(res => setGroupSettings(res.data));
  };

  useEffect(load, [id, gid, apiBase]);

  const saveMeta = () => {
    axios.put(`/api/${apiBase}/gate-groups/${gid}`, {
      name: draftName,
      description: draftDesc,
    }).then(() => { setEditingMeta(false); load(); })
      .catch(err => setError(err.response?.data?.error || 'שגיאה בשמירה'));
  };

  const removeFromGroup = (gateId) => {
    axios.put(`/api/${apiBase}/gates/${gateId}/group`, { group_id: null })
      .then(load);
  };

  const deleteGroup = () => {
    if (!window.confirm(`למחוק את הקבוצה "${group?.name}"? השערים בה יישארו אבל יתנתקו מהקבוצה.`)) return;
    axios.delete(`/api/${apiBase}/gate-groups/${gid}`)
      .then(() => navigate(`/sites/${id}/groups`));
  };

  if (group === null) return <div className="loading">טוען...</div>;

  const memberGates = siteGates.filter(g => group.gate_ids.includes(g.id));
  const candidateGates = siteGates.filter(g => !group.gate_ids.includes(g.id));

  return (
    <>
      {apiBase === 'customer' ? (
        <CustomerBreadcrumb items={[
          { label: 'מפת האתר', to: `/sites/${id}` },
          { label: 'קבוצות', to: `/sites/${id}/groups` },
          { label: group.name },
        ]} />
      ) : (
        <div style={{ marginBottom: '0.6rem' }}>
          <Link to={`/sites/${id}/groups`} style={{ fontSize: '0.85rem', color: '#6b7280' }}>
            ← חזרה לקבוצות
          </Link>
        </div>
      )}

      {!editingMeta ? (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
          <div>
            <h2 style={{ fontSize: '1.15rem', fontWeight: 600, margin: 0, marginBottom: '0.3rem' }}>{group.name}</h2>
            {group.description && (
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280' }}>{group.description}</p>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <button
              onClick={() => setEditingMeta(true)}
              style={{ padding: '0.35rem 0.75rem', border: '1px solid #d1d5db', background: '#fff', borderRadius: '5px', fontSize: '0.8rem', cursor: 'pointer' }}
            >
              ערוך
            </button>
            <button
              onClick={deleteGroup}
              style={{ padding: '0.35rem 0.75rem', border: '1px solid #fecaca', background: '#fef2f2', color: '#b91c1c', borderRadius: '5px', fontSize: '0.8rem', cursor: 'pointer' }}
            >
              מחק
            </button>
          </div>
        </div>
      ) : (
        <div style={{ marginBottom: '1.25rem', padding: '0.85rem', border: '1px solid #e5e7eb', borderRadius: '6px', background: '#f9fafb' }}>
          <input
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            style={{ width: '100%', boxSizing: 'border-box', padding: '0.4rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '5px', fontSize: '0.9rem', marginBottom: '0.5rem' }}
            placeholder="שם"
          />
          <input
            value={draftDesc}
            onChange={(e) => setDraftDesc(e.target.value)}
            style={{ width: '100%', boxSizing: 'border-box', padding: '0.4rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '5px', fontSize: '0.9rem', marginBottom: '0.5rem' }}
            placeholder="תיאור"
          />
          <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
            <button
              onClick={() => { setEditingMeta(false); setDraftName(group.name); setDraftDesc(group.description || ''); }}
              style={{ padding: '0.4rem 0.85rem', background: 'transparent', border: 'none', color: '#6b7280', cursor: 'pointer' }}
            >
              ביטול
            </button>
            <button
              onClick={saveMeta}
              style={{ padding: '0.4rem 0.85rem', background: '#1f2937', color: '#fff', border: 'none', borderRadius: '5px', fontSize: '0.85rem', cursor: 'pointer' }}
            >
              שמור
            </button>
          </div>
        </div>
      )}

      {error && <div style={{ color: '#dc2626', fontSize: '0.85rem', marginBottom: '0.85rem' }}>{error}</div>}

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <button
          onClick={() => setConfigOpen(true)}
          style={{ padding: '0.55rem 0.95rem', border: '1px solid #d1d5db', background: '#fff', borderRadius: '6px', fontSize: '0.9rem', cursor: 'pointer' }}
        >
          ⚙️ הגדרות הקבוצה
        </button>
        <button
          onClick={() => setScheduleOpen(true)}
          style={{ padding: '0.55rem 0.95rem', border: '1px solid #d1d5db', background: '#fff', borderRadius: '6px', fontSize: '0.9rem', cursor: 'pointer' }}
        >
          🕒 שעות פעילות הקבוצה
        </button>
      </div>

      <h3 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.6rem' }}>
        שערים בקבוצה ({memberGates.length})
      </h3>
      <GatesTable
        gates={memberGates}
        onRowClick={(g) => navigate(apiBase === 'customer' ? `/sites/${id}` : `/gates/${g.id}`)}
        extraHeaderCells={<th style={{ width: '1%' }}></th>}
        renderExtraCells={(g) => (
          <td onClick={e => e.stopPropagation()}>
            <button
              onClick={() => removeFromGroup(g.id)}
              style={{ padding: '0.3rem 0.7rem', border: '1px solid #fecaca', background: '#fef2f2', color: '#b91c1c', borderRadius: '4px', fontSize: '0.78rem', cursor: 'pointer' }}
            >
              הסר
            </button>
          </td>
        )}
      />

      {candidateGates.length > 0 && (
        <>
          <button
            onClick={() => setShowAddGates(s => !s)}
            style={{ marginTop: '1rem', padding: '0.5rem 0.95rem', border: '1px solid #d1d5db', background: '#fff', borderRadius: '6px', fontSize: '0.85rem', cursor: 'pointer' }}
          >
            {showAddGates ? 'סגור הוספת שערים' : '+ הוסף שערים לקבוצה'}
          </button>
          {showAddGates && (
            <div style={{ marginTop: '0.85rem', padding: '0.85rem', border: '1px solid #e5e7eb', borderRadius: '6px' }}>
              <p style={{ margin: 0, marginBottom: '0.65rem', fontSize: '0.85rem', color: '#6b7280' }}>
                לחץ על שער כדי להוסיף אותו לקבוצה.
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {candidateGates.map(g => (
                  <li key={g.id} style={{ marginBottom: '0.35rem' }}>
                    <button
                      onClick={() => {
                        axios.put(`/api/${apiBase}/gates/${g.id}/group`, { group_id: Number(gid) })
                          .then(load);
                      }}
                      style={{ display: 'block', width: '100%', padding: '0.55rem 0.8rem', border: '1px solid #e5e7eb', background: '#fff', borderRadius: '5px', fontSize: '0.85rem', textAlign: 'right', cursor: 'pointer' }}
                    >
                      {g.name} {g.group_id && <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>(שייך כעת ל: {g.group_name})</span>}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {configOpen && groupSettings && (
        <GateConfigMenu
          gateId={null}
          gateIds={null}
          groupId={Number(gid)}
          initialSettings={groupSettings}
          apiBase={apiBase}
          onClose={() => setConfigOpen(false)}
          onApplied={() => {
            axios.get(`/api/${apiBase}/gate-groups/${gid}/settings`)
              .then(res => setGroupSettings(res.data));
          }}
        />
      )}

      {scheduleOpen && (
        <GateScheduleEditor
          gateId={null}
          gateIds={null}
          groupId={Number(gid)}
          apiBase={apiBase}
          initialAlertEnabled={!!groupSettings?.unauthorized_open_alert_enabled}
          onClose={() => setScheduleOpen(false)}
          onApplied={() => {
            axios.get(`/api/${apiBase}/gate-groups/${gid}/settings`)
              .then(res => setGroupSettings(res.data));
          }}
        />
      )}
    </>
  );
}


export default GateGroupsPage;
